import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { validateAuthCode } from "../api/services/authService";

const LoginWithCode: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const { setToken } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Single-use auth codes get consumed on the first call — guard against
  // React StrictMode's double-effect invocation in dev, which otherwise
  // burns the code on attempt #1 and reports "expired" on attempt #2.
  const hasValidated = useRef(false);

  useEffect(() => {
    if (hasValidated.current) return;
    hasValidated.current = true;

    const handleCodeValidation = async () => {
      if (!code) {
        setError(t("loginWithCode.errorNoCode"));
        setLoading(false);
        return;
      }

      try {
        const response = await validateAuthCode(code);
        // eslint-disable-next-line no-console
        console.log("[LoginWithCode] response", response.status, response.data);
        if (response.data.token) {
          setToken(response.data.token);
          // Optional `?next=/renewal/…` so a magic link can drop the tenant
          // straight onto the page it was about. Same-origin paths only —
          // this value comes from the URL, so an unchecked redirect here
          // would bounce a just-authenticated user anywhere.
          const next = new URLSearchParams(window.location.search).get("next");
          const safe =
            next && next.startsWith("/") && !next.startsWith("//")
              ? next
              : "/dashboard";
          navigate(safe, { replace: true });
        } else {
          // eslint-disable-next-line no-console
          console.warn("[LoginWithCode] no token in response", response.data);
          setError(t("loginWithCode.errorInvalidResponse"));
        }
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error("[LoginWithCode] caught", err, err?.response?.status, err?.response?.data);
        setError(
          err.response?.data?.message || t("loginWithCode.errorInvalid"),
        );
      } finally {
        setLoading(false);
      }
    };

    handleCodeValidation();
  }, [code, setToken, navigate, t]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div
            className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"
            role="status"
          >
            <span className="sr-only">{t("loginWithCode.loadingSr")}</span>
          </div>
          <p className="mt-4">{t("loginWithCode.authenticating")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div
            className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger"
            role="alert"
          >
            <h4 className="font-semibold">{t("loginWithCode.failed")}</h4>
            <p>{error}</p>
          </div>
          <button
            className="mt-4 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
            onClick={() => navigate("/login")}
          >
            {t("loginWithCode.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default LoginWithCode;
